/**
 * Canary flows (docs/event-model.md §6, ADR-0011): a secret reaching a
 * network sink. Findings are grouped per (canary, destination) and escalate
 * every sink event they cite.
 */
import type { CanaryKind, DataFlow } from "../../canary";
import { type AuditEvent, CANARY_ENCODINGS, type Severity } from "../../events";
import { originOf } from "../../url";
import type { AnalysisInput, Rule, RuleEscalation, RuleFinding, RuleOutput } from "../types";

/** Form-field canaries, typed by simulated user activity (`input[name=cc-number]`). */
const isFormSource = (source: string) => /^(input|textarea|select)[[#]/.test(source);

/** Analytics and device ids leave pages routinely; real secrets shouldn't. */
const exfilSeverity = (kind: CanaryKind): Severity =>
  kind === "tracking" || kind === "device" ? "suspicious" : "critical";

/** The first non-sink event before `before` whose result shows the canary's value: where it was read. */
function readEvidence(events: readonly AuditEvent[], value: string, before: number): number[] {
  for (const e of events) {
    if (e.seq >= before) break;
    if (e.network || e.category === "messaging" || e.result === undefined) continue;
    const r = e.result;
    const text =
      typeof r === "string" ? r : r && typeof r === "object" && "$str" in r ? r.$str : "";
    if (text.includes(value)) return [e.seq];
  }
  return [];
}

interface Group {
  flows: DataFlow[];
  destination: string;
}

/** Flows grouped per (canary, destination), in first-seen order. */
function group(flows: readonly DataFlow[], destination: (f: DataFlow) => string): Group[] {
  const groups = new Map<string, Group>();
  for (const f of flows) {
    const d = destination(f);
    const key = `${f.canary}\u0000${d}`;
    const g = groups.get(key) ?? { flows: [], destination: d };
    g.flows.push(f);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/** Encodings across flows, in `CANARY_ENCODINGS` order. */
const encodingsOf = (flows: readonly DataFlow[]) =>
  CANARY_ENCODINGS.filter((e) => flows.some((f) => f.encodings.includes(e)));

const isPartial = (flows: readonly DataFlow[]) => flows.every((f) => f.partial);

/** ` (base64, hex)`, ` (partial)`, or nothing for raw-only flows. */
function how(flows: readonly DataFlow[]): string {
  const notes: string[] = encodingsOf(flows).filter((e) => e !== "raw");
  if (isPartial(flows)) notes.push("partial");
  return notes.length > 0 ? ` (${notes.join(", ")})` : "";
}

interface GroupFinding {
  severity: Severity;
  title: string;
  extraTags?: string[];
  escalate: boolean;
}

/** Findings (and escalations) for flow groups. */
function report(
  input: AnalysisInput,
  groups: readonly Group[],
  describe: (g: Group, first: DataFlow) => GroupFinding,
): RuleOutput {
  const findings: RuleFinding[] = [];
  const escalations: RuleEscalation[] = [];
  for (const g of groups) {
    const first = g.flows[0] as DataFlow;
    const { severity, title, extraTags = [], escalate } = describe(g, first);
    const sinks = g.flows.map((f) => f.seq);
    const value = input.registry.canaries.find((c) => c.id === first.canary)?.value ?? "";
    const tags = [first.kind, ...encodingsOf(g.flows), ...(isPartial(g.flows) ? ["partial"] : [])];
    findings.push({
      severity,
      title,
      target: g.destination,
      tags: [...tags, ...extraTags],
      evidence: [...readEvidence(input.events, value, first.seq), ...sinks],
    });
    if (escalate) for (const seq of sinks) escalations.push({ seq, severity });
  }
  return { findings, escalations };
}

const eventAt = (input: AnalysisInput, seq: number) => input.events.find((e) => e.seq === seq);

const isNetworkFlow = (input: AnalysisInput, f: DataFlow) =>
  eventAt(input, f.seq)?.network !== undefined;

/** Network flows to anywhere but the page's own origin. */
function thirdPartyFlows(input: AnalysisInput): DataFlow[] {
  const page = originOf(input.context.url);
  return input.flows.filter((f) => isNetworkFlow(input, f) && f.origin !== page);
}

const destination = (f: DataFlow) => f.origin ?? f.sink;

export const CANARY_RULES: readonly Rule[] = [
  {
    id: "canary.exfiltration",
    explanation:
      "A honeytoken planted in the page's cookies, storage, or URL appeared in a request to another origin, so the script sends that secret off the page. Analytics ids are suspicious; credentials and personal data are critical.",
    apply: (input) =>
      report(
        input,
        group(
          thirdPartyFlows(input).filter((f) => !isFormSource(f.source)),
          destination,
        ),
        (g, f) => ({
          severity: exfilSeverity(f.kind),
          title: `${f.source} sent to ${g.destination}${how(g.flows)}`,
          escalate: true,
        }),
      ),
  },
  {
    id: "canary.form-exfiltration",
    explanation:
      "A value the simulated user typed into a sensitive form field (card, password, contact details) appeared in a request to another origin: the classic form-skimmer pattern.",
    apply: (input) =>
      report(
        input,
        group(
          thirdPartyFlows(input).filter((f) => isFormSource(f.source)),
          destination,
        ),
        (g, f) => ({
          severity: exfilSeverity(f.kind),
          title: `Typed ${f.kind} data from ${f.source} sent to ${g.destination}${how(g.flows)}`,
          escalate: true,
        }),
      ),
  },
  {
    id: "canary.first-party",
    explanation:
      "A secret was sent to the page's own origin. Sites do this all the time (session headers, form posts), so it is only notable, but it shows the script handles the secret.",
    apply: (input) => {
      const page = originOf(input.context.url);
      const flows = input.flows.filter((f) => isNetworkFlow(input, f) && f.origin === page);
      return report(input, group(flows, destination), (_, f) => ({
        severity: "notable",
        title: `${f.source} sent to the page's own origin`,
        extraTags: ["first-party"],
        escalate: false,
      }));
    },
  },
  {
    id: "canary.message-leak",
    explanation:
      "A secret was posted as a message (postMessage or BroadcastChannel), where other frames or tabs, possibly on other origins, can receive it.",
    apply: (input) => {
      const flows = input.flows.filter((f) => eventAt(input, f.seq)?.category === "messaging");
      return report(
        input,
        group(flows, (f) => f.sink),
        (_, f) => ({
          severity: "suspicious",
          title: `${f.source} posted through ${f.sink}`,
          escalate: true,
        }),
      );
    },
  },
];
